import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";

// Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import CheckBox from "@limitless/modern/inputs/checkbox/CheckBox";
import { useExpertRatingContext } from "@limitless/expert";
import CommonFilterPoper from "../filters/CommonFilterPoper";
import { SimpleAccordion } from "@limitless/portal";
import Divider from "@limitless/modern/data-display/divider/Divider";
import Typography from "@limitless/modern/data-display/typography/Typography";

export const checkBoxStyling = {
    padding: "2px 9px 6px 0px !important"
};

const CommonFilterAccordian = ({ title, children }) => {
    const classes = commonRatingFilter();

    return (
        <SimpleAccordion
            key={title}
            descColor="initial"
            variant="body2"
            rootStyle={classes.summaryRoot}
            classes={classes}
            title={title}
        >
            <Grid container display="flex" flexDirection="column" sx={{ marginBottom: 1 }}>
                {children}
            </Grid>
        </SimpleAccordion>
    );
};

const Filters = ({ clickAwayClose }) => {
    const { ratingFilterExists, ratingMessageFilters, updateRatingFilters, clearRatingState } =
        useExpertRatingContext();

    const {
        custRatingHundred,
        custRatingEighty,
        custRatingFifty,
        custRatingZero,
        qualityRatingHundrad,
        qualityRatingEighty,
        qualityRatingFifty,
        qualityRatingZero
    } = ratingMessageFilters;

    const custRatingArray = React.useMemo(() => {
        return [
            {
                onLabel: "Included in rating calculation",
                offLabel: "Included in rating calculation",
                onChange: () => updateRatingFilters({ custRatingHundred: !custRatingHundred }),
                checked: custRatingHundred,
                id: "custRatingHundredFilterCheckBox"
            },
            {
                onLabel: "100% - 80%",
                offLabel: "100% - 80%",
                onChange: () => updateRatingFilters({ custRatingEighty: !custRatingEighty }),
                checked: custRatingEighty,
                id: "custRatingEightyCheckbox"
            },
            {
                onLabel: "80% - 50%",
                offLabel: "80% - 50%",
                onChange: () => updateRatingFilters({ custRatingFifty: !custRatingFifty }),
                checked: custRatingFifty,
                id: "custRatingFiftyCheckBox"
            },
            {
                onLabel: "50% - 0%",
                offLabel: "50% - 0%",
                onChange: () => updateRatingFilters({ custRatingZero: !custRatingZero }),
                checked: custRatingZero,
                id: "custRatingZeroCheckBox"
            }
        ];
    }, [ratingMessageFilters]);

    const qualityRatingArray = React.useMemo(() => {
        return [
            {
                onLabel: "Included in rating calculation",
                offLabel: "Included in rating calculation",
                onChange: () => updateRatingFilters({ qualityRatingHundrad: !qualityRatingHundrad }),
                checked: qualityRatingHundrad,
                id: "qualityHundradCheckBox"
            },
            {
                onLabel: "100% - 80%",
                offLabel: "100% - 80%",
                onChange: () => updateRatingFilters({ qualityRatingEighty: !qualityRatingEighty }),
                checked: qualityRatingEighty,
                id: "qualityEightyCheckbox"
            },
            {
                onLabel: "80% - 50%",
                offLabel: "80% - 50%",
                onChange: () => updateRatingFilters({ qualityRatingFifty: !qualityRatingFifty }),
                checked: qualityRatingFifty,
                id: "qualityFiftyCheckBox"
            },
            {
                onLabel: "50% - 0%",
                offLabel: "50% - 0%",
                onChange: () => updateRatingFilters({ qualityRatingZero: !qualityRatingZero }),
                checked: qualityRatingZero,
                id: "qualityZeroCheckBox"
            }
        ];
    }, [ratingMessageFilters]);

    return (
        <CommonFilterPoper
            label="Filter ratings"
            clickAwayClose={clickAwayClose}
            filterExists={ratingFilterExists}
            title={"Filter by:"}
            clearFilters={() => clearRatingState("RESET_MESSAGES_FILTERS")}
        >
            <CommonFilterAccordian title={"Customer Rating"}>
                {custRatingArray.map(value => {
                    return (
                        <CheckBox
                            key={value.id}
                            onLabel={value.onLabel}
                            offLabel={value.offLabel}
                            onChange={value.onChange}
                            checked={value.checked}
                            variantStyling={checkBoxStyling}
                            id={value.id}
                        />
                    );
                })}
            </CommonFilterAccordian>
            <Grid item xs={12}>
                <Divider>
                    <Typography variant="subtitle2">or</Typography>
                </Divider>
            </Grid>
            <CommonFilterAccordian title={"Quality Rating"}>
                {qualityRatingArray.map(qualityRating => {
                    return (
                        <CheckBox
                            key={qualityRating.id}
                            onLabel={qualityRating.onLabel}
                            offLabel={qualityRating.offLabel}
                            onChange={qualityRating.onChange}
                            checked={qualityRating.checked}
                            variantStyling={checkBoxStyling}
                            id={qualityRating.id}
                        />
                    );
                })}
            </CommonFilterAccordian>
        </CommonFilterPoper>
    );
};
export const commonRatingFilter = makeStyles(theme => ({
    accordionStyling: {
        maxWidth: "inherit",
        width: "100%",
        boxShadow: "none !important",
        margin: "0px",
        "&::before": { height: 0 },
        "&.Mui-expanded": {
            margin: "0px 0"
        }
    },
    icon: {
        paddingTop: "8px",
        width: 16,
        height: 16,
        color: theme.palette.primary.dark
    },
    summaryRoot: {
        padding: 0,
        wordBreak: "break-word",
        hyphens: "auto"
    },
    italicTypography: {
        fontSize: "0.875rem",
        fontWeight: 400,
        fontStyle: "italic",
        marginTop: "8px",
        paddingRight: "8px",
        whiteSpace: "pre-line"
    },
    divider: {
        marginBottom: "16px",
        marginRight: "32px"
    }
}));

Filters.propTypes = {
    clickAwayClose: PropTypes.bool
};

CommonFilterAccordian.propTypes = {
    title: PropTypes.string,
    showDivider: PropTypes.bool,
    children: PropTypes.node
};

export default Filters;
